/**
 * Per-session UI + run state, held outside React so it survives navigation.
 *
 * The chat shell mounts one ChatView and one right-hand panel stack at a time,
 * but a session's life is longer than its time on screen: a clerk answer keeps
 * streaming after you switch away, a composer draft is still half-typed, a
 * finished pipeline is still worth reopening. All of that is keyed by
 * `<instance>/<session id>` here; the components are pure views over it.
 *
 * Every writer replaces the slot object, so `useSyncExternalStore` snapshots
 * stay referentially stable while nothing changes.
 */

import { useSyncExternalStore } from "react";
import type { Message } from "./api";
import type { LetterFormSpec, PipelineProgress, PipelineRun } from "./sse";


export interface PendingTool {
  name: string;
  input: unknown;
  status: "running" | "done" | "error";
}

export interface Attachment {
  name: string;
  key: string;
}

/** Chat pane state for one session: transcript, composer, and the live run. */
export interface ChatState {
  loaded: boolean;
  messages: Message[];
  draft: string;
  attached: Attachment[];
  uploading: boolean;
  busy: boolean;
  streaming: string;
  tools: PendingTool[];
  progress: PipelineProgress | null;
  /** Live all-in-one letter intake card (SSE `form` event); cleared when a
   *  new ask starts or its submission is sent. */
  form: LetterFormSpec | null;
  error: string | null;
}

/** Right-column state for one session: which windows are open and what's in them. */
export interface PanelState {
  previewPageId: string | null;
  previewAnchor?: string;
  previewOpen: boolean;
  run: PipelineRun | null;
  runOpen: boolean;
  pipelinesOpen: boolean;
}

const EMPTY_CHAT: ChatState = {
  loaded: false,
  messages: [],
  draft: "",
  attached: [],
  uploading: false,
  busy: false,
  streaming: "",
  tools: [],
  progress: null,
  form: null,
  error: null,
};

const EMPTY_PANEL: PanelState = {
  previewPageId: null,
  previewAnchor: undefined,
  previewOpen: false,
  run: null,
  runOpen: false,
  pipelinesOpen: false,
};

export function sessionKey(instance: string, sid: string): string {
  return `${instance}/${sid}`;
}

type Listener = () => void;

const listeners = new Set<Listener>();
const chats = new Map<string, ChatState>();
const panels = new Map<string, PanelState>();
let busyKeys: ReadonlySet<string> = new Set();

function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

function emit() {
  for (const l of listeners) l();
}

/** Recompute the busy set only when membership actually changed, so the sidebar
 *  snapshot keeps its identity between unrelated updates. */
function syncBusy() {
  const next = new Set<string>();
  for (const [k, s] of chats) if (s.busy) next.add(k);
  if (next.size === busyKeys.size && [...next].every(k => busyKeys.has(k))) return;
  busyKeys = next;
}


export function getChat(key: string): ChatState {
  return chats.get(key) ?? EMPTY_CHAT;
}

export function updateChat(
  key: string,
  patch: Partial<ChatState> | ((s: ChatState) => Partial<ChatState>),
) {
  const cur = getChat(key);
  chats.set(key, { ...cur, ...(typeof patch === "function" ? patch(cur) : patch) });
  syncBusy();
  emit();
}

export function getPanel(key: string): PanelState {
  return panels.get(key) ?? EMPTY_PANEL;
}

export function updatePanel(
  key: string,
  patch: Partial<PanelState> | ((s: PanelState) => Partial<PanelState>),
) {
  const cur = getPanel(key);
  panels.set(key, { ...cur, ...(typeof patch === "function" ? patch(cur) : patch) });
  emit();
}

/** Drop a deleted session's state so it can't resurface. */
export function forgetSession(key: string) {
  chats.delete(key);
  panels.delete(key);
  syncBusy();
  emit();
}


export function useChat(key: string): ChatState {
  return useSyncExternalStore(subscribe, () => getChat(key));
}

export function usePanel(key: string): PanelState {
  return useSyncExternalStore(subscribe, () => getPanel(key));
}

/** Session keys with a clerk answer in flight — drives the sidebar activity dot. */
export function useBusySessions(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, () => busyKeys);
}
